import { QUAD_ATTRIB_LOCATION } from './quad';

const UV_ATTRIB_LOCATION = 1;
const FLOATS_PER_VERTEX = 4;

/**
 * Dynamic triangle grid for the warp pass. Vertices interleave the destination
 * position and the source UV, so the vertex stage needs no matrix beyond the
 * target one and every warp shape is one draw call.
 */
export class MeshGeometry {
  private readonly gl: WebGL2RenderingContext;
  private readonly vao: WebGLVertexArrayObject;
  private readonly vertices: WebGLBuffer;
  private readonly indices: WebGLBuffer;
  private cells = 0;
  private indexCount = 0;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
    this.vao = gl.createVertexArray()!;
    this.vertices = gl.createBuffer()!;
    this.indices = gl.createBuffer()!;

    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vertices);
    const stride = FLOATS_PER_VERTEX * 4;
    gl.enableVertexAttribArray(QUAD_ATTRIB_LOCATION);
    gl.vertexAttribPointer(QUAD_ATTRIB_LOCATION, 2, gl.FLOAT, false, stride, 0);
    gl.enableVertexAttribArray(UV_ATTRIB_LOCATION);
    gl.vertexAttribPointer(UV_ATTRIB_LOCATION, 2, gl.FLOAT, false, stride, 8);
    gl.bindVertexArray(null);
  }

  /**
   * Uploads a `cells` x `cells` grid. `sample` returns, for parameters 0..1, the
   * destination position and the source UV of that vertex.
   */
  upload(
    cells: number,
    sample: (s: number, t: number) => { pos: [number, number]; uv: [number, number] },
  ): void {
    const { gl } = this;
    const side = cells + 1;
    const data = new Float32Array(side * side * FLOATS_PER_VERTEX);
    let at = 0;
    for (let row = 0; row < side; row += 1) {
      for (let column = 0; column < side; column += 1) {
        const { pos, uv } = sample(column / cells, row / cells);
        data[at] = pos[0];
        data[at + 1] = pos[1];
        data[at + 2] = uv[0];
        data[at + 3] = uv[1];
        at += FLOATS_PER_VERTEX;
      }
    }

    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vertices);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.DYNAMIC_DRAW);

    // The index pattern only depends on the cell count, so it is rebuilt rarely.
    if (cells !== this.cells) {
      const indices = new Uint16Array(cells * cells * 6);
      let out = 0;
      for (let row = 0; row < cells; row += 1) {
        for (let column = 0; column < cells; column += 1) {
          const topLeft = row * side + column;
          const topRight = topLeft + 1;
          const bottomLeft = topLeft + side;
          const bottomRight = bottomLeft + 1;
          indices.set([topLeft, topRight, bottomLeft, topRight, bottomRight, bottomLeft], out);
          out += 6;
        }
      }
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indices);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);
      this.cells = cells;
      this.indexCount = indices.length;
    } else {
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indices);
    }
    gl.bindVertexArray(null);
  }

  draw(): void {
    const { gl } = this;
    if (this.indexCount === 0) return;
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indices);
    gl.drawElements(gl.TRIANGLES, this.indexCount, gl.UNSIGNED_SHORT, 0);
    gl.bindVertexArray(null);
  }

  dispose(): void {
    this.gl.deleteVertexArray(this.vao);
    this.gl.deleteBuffer(this.vertices);
    this.gl.deleteBuffer(this.indices);
  }
}
